import { extractClipSlugAndId } from 'utils/HighlightsUtils';
import { fetchHighlightsMetadata } from 'api/highlights';
import type { HighlightsMetadata } from 'api/highlights';
import type { State } from 'state/buildInitialState';

/**
 * Service to handle clip metadata operations for legacy app
 */
export class HighlightsMetadataService {
  /**
   * Fetches clip metadata by highlights slug
   * Extracts the clip ID from the slug and fetches metadata from Genuin API
   *
   * @param highlightsSlug - The combined slug and ID (e.g., "hello-world_abc123-def4-5678-90ab-cdef12345678")
   * @param state - Redux state to get config from
   * @returns Promise with clip metadata or null if invalid slug
   */
  static async getHighlightsMetadata(
    highlightsSlug?: string,
    state?: State,
  ): Promise<HighlightsMetadata | null> {
    if (!highlightsSlug || !state) {
      return null;
    }

    const { clipId } = extractClipSlugAndId(highlightsSlug);

    if (!clipId) {
      return null;
    }

    try {
      const metadata = await fetchHighlightsMetadata({ clipId, state });

      // Return null if there's an error in the response
      if (metadata.error) {
        return null;
      }

      return metadata;
    } catch {
      return null;
    }
  }

  /**
   * Checks if a highlights slug has a valid clip ID
   *
   * @param highlightsSlug - The combined slug and ID
   * @returns true if the slug contains a valid UUID
   */
  static hasValidClipId(highlightsSlug?: string): boolean {
    if (!highlightsSlug) {
      return false;
    }

    const { clipId } = extractClipSlugAndId(highlightsSlug);
    return Boolean(clipId);
  }
}
